import React, { useEffect, useState } from "react";

import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import {
  TextField,
  Box,
  Chip,
  IconButton,
  Button,
  Typography,
  Stack,
  Paper,
  Tooltip,
  Divider
} from "@mui/material";

import { generateUUID } from "../../generic-functions/generate-uuid";
import useQuestion from "../../dialog-components/QuestionDialog";

import "./Settings.scss";

interface ListItemsProps {
  currentList: { id: string; name: string; category: string; }[];
  setCurrentList: React.Dispatch<
    React.SetStateAction<{ id: string; name: string; category: string; }[]>
  >;
  chosenCategory: string;
  itemsKind: "category" | "name";
}

export const ListItems: React.FC<ListItemsProps> = (props) => {
  const { currentList, setCurrentList, chosenCategory, itemsKind } = props;
  const question = useQuestion();
  const [isNewItem, seIsNewItem] = useState<boolean>(false);
  const [newItem, setNewItem] = useState<string>("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editedValue, setEditedValue] = useState<string>("");

  const filteredList =
    itemsKind === "category"
      ? Array.from(new Set(currentList.map((item) => item.category)))
      : currentList
        .filter((item) => item.category === chosenCategory)
        .map((item) => item.name);

  const addListItem = () => {
    const trimmed = newItem.trim();
    if (!trimmed) return;

    if (itemsKind === "category") {
      const alreadyExists = currentList.some(
        (item) => item.category === trimmed
      );
      if (!alreadyExists) {
        setCurrentList([
          ...currentList,
          { id: generateUUID(), name: "", category: trimmed },
        ]);
        setNewItem("");
        seIsNewItem(false);
      }
    } else if (itemsKind === "name") {
      const alreadyExists = currentList.some(
        (item) => item.name === trimmed && item.category === chosenCategory
      );
      if (!alreadyExists) {
        setCurrentList([
          ...currentList,
          { id: generateUUID(), name: trimmed, category: chosenCategory },
        ]);
        setNewItem("");
        seIsNewItem(false);
      }
    }
  };

  const handleEditListItem = (id: string, value: string) => {
    setEditingId(id);
    setEditedValue(value);
  };

  const handleSaveEditListItem = () => {
    setCurrentList(
      currentList.map((item) =>
        item.id === editingId
          ? itemsKind === "category"
            ? { ...item, category: editedValue }
            : { ...item, name: editedValue }
          : item
      )
    );
    setEditingId(null);
    setEditedValue("");
  };

  const handleDeleteListItem = async (id: string, value: string) => {
    const confirmMessage =
      itemsKind === "category"
        ? `האם אתה בטוח שברצונך למחוק את הקטגוריה "${value}"?`
        : `האם אתה בטוח שברצונך למחוק את האפשרות "${value}"?`;

    if (await question(confirmMessage)) {
      setCurrentList(
        currentList.filter((item) =>
          itemsKind === "category"
            ? item.category !== value
            : !(item.id === id && item.name === value)
        )
      );
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      addListItem();
    } else if (e.key === "Escape") {
      seIsNewItem(false);
      setNewItem("");
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveEditListItem();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  return (
    <Box>
      {/* Current Items Display */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: "#64748b",
            mb: 2,
            fontWeight: 600,
            fontSize: "0.9rem"
          }}
        >
          {itemsKind === "category" ? "קטגוריות קיימות" : "אפשרויות קיימות"}
        </Typography>

        {filteredList.length > 0 ? (
          <Stack direction="row" flexWrap="wrap" gap={1.5}>
            {filteredList.map((value, index) => {
              const id =
                itemsKind === "category"
                  ? currentList.find((item) => item.category === value)?.id || ""
                  : currentList.find(
                    (item) => item.name === value && item.category === chosenCategory
                  )?.id || "";

              return (
                <Paper
                  key={id || index}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                    background: "rgba(255, 255, 255, 0.8)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "rgba(255, 107, 107, 0.3)",
                      boxShadow: "0 4px 12px rgba(255, 107, 107, 0.15)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {editingId === id ? (
                    <Box sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        autoFocus
                        size="small"
                        value={editedValue}
                        onChange={(e) => setEditedValue(e.target.value)}
                        onKeyDown={handleEditKeyDown}
                        sx={{
                          minWidth: "120px",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            fontSize: "0.9rem",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#ff6b6b",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#ff6b6b",
                                borderWidth: 2,
                              },
                            },
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={handleSaveEditListItem}
                        sx={{
                          color: "#4caf50",
                          "&:hover": {
                            background: "rgba(76, 175, 80, 0.1)",
                          },
                        }}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleCancelEdit}
                        sx={{
                          color: "#f44336",
                          "&:hover": {
                            background: "rgba(244, 67, 54, 0.1)",
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#1e293b",
                          fontSize: "0.9rem",
                          minWidth: "80px",
                        }}
                      >
                        {value}
                      </Typography>
                      <Tooltip title="עריכה">
                        <IconButton
                          size="small"
                          onClick={() => handleEditListItem(id, value)}
                          sx={{
                            color: "#64748b",
                            "&:hover": {
                              background: "rgba(100, 116, 139, 0.1)",
                              color: "#ff6b6b",
                            },
                          }}
                        >
                          <ModeEditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="מחיקה">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteListItem(id, value)}
                          sx={{
                            color: "#64748b",
                            "&:hover": {
                              background: "rgba(100, 116, 139, 0.1)",
                              color: "#f44336",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Stack>
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              color: "#94a3b8",
              background: "rgba(0, 0, 0, 0.02)",
              borderRadius: 2,
              border: "1px dashed rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography variant="body2" sx={{ fontSize: "0.9rem" }}>
              {itemsKind === "category"
                ? "אין קטגוריות מוגדרות עדיין"
                : "אין אפשרויות מוגדרות עדיין"
              }
            </Typography>
          </Box>
        )}
      </Box>

      {/* Add New Item Section */}
      <Box>
        {!isNewItem ? (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => seIsNewItem(true)}
            sx={{
              borderColor: "rgba(255, 107, 107, 0.3)",
              color: "#ff6b6b",
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#ff6b6b",
                background: "rgba(255, 107, 107, 0.05)",
                transform: "translateY(-1px)",
              },
            }}
          >
            {itemsKind === "category"
              ? "הוספת קטגוריה חדשה"
              : "הוספת אפשרות חדשה"}
          </Button>
        ) : (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid rgba(255, 107, 107, 0.2)",
              background: "rgba(255, 255, 255, 0.9)",
              p: 2,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                autoFocus
                size="small"
                value={newItem}
                placeholder={
                  itemsKind === "category"
                    ? "שם הקטגוריה החדשה"
                    : "שם האפשרות החדשה"
                }
                onChange={(e) => setNewItem(e.target.value)}
                onKeyDown={handleKeyDown}
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ff6b6b",
                      },
                    },
                    "&.Mui-focused": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ff6b6b",
                        borderWidth: 2,
                      },
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={addListItem}
                disabled={!newItem.trim()}
                sx={{
                  background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
                  color: "white",
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  "&:hover": {
                    background: "linear-gradient(135deg, #ff5252 0%, #e64a19 100%)",
                    transform: "translateY(-1px)",
                  },
                  "&:disabled": {
                    background: "#e5e7eb",
                    color: "#9ca3af",
                  },
                }}
              >
                הוספה
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  seIsNewItem(false);
                  setNewItem("");
                }}
                sx={{
                  color: "#64748b",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    background: "rgba(100, 116, 139, 0.1)",
                  },
                }}
              >
                ביטול
              </Button>
            </Stack>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default ListItems;
